import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';

// URL-encoded filters per docs/ui/operator-ui-tds.md §4.1: "URL-encoded so
// links are shareable". The list route reads them via useSearchParams; this
// component is the controlled form binding.
//
// Bucket is controlled separately by the tab-style picker in the active list
// (open / in-progress / needs-attention). Historic doesn't expose buckets.

export interface FilterBarProps {
  /** When true, hides the bucket toggle (historic page). */
  hideBuckets?: boolean;
}

const STATUS_OPTIONS = [
  '',
  'IN_PROGRESS',
  'AWAITING_DEMAND',
  'AWAITING_BOOKING',
  'BOOKING_FAILED',
  'RESOLVED',
];

export function FilterBar({ hideBuckets = false }: FilterBarProps) {
  const { t } = useTranslation('cases');
  const [params, setParams] = useSearchParams();

  const update = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
      <Filter label={t('filters.airport')}>
        <input
          type="text"
          maxLength={3}
          placeholder="SCL"
          value={params.get('airport') ?? ''}
          onChange={(e) => update('airport', e.target.value.toUpperCase())}
          className="w-20 rounded-md border border-input bg-background px-2 py-1 uppercase"
        />
      </Filter>

      <Filter label={t('filters.status')}>
        <select
          value={params.get('status') ?? ''}
          onChange={(e) => update('status', e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-1"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || t('filters.any')}
            </option>
          ))}
        </select>
      </Filter>

      <Filter label={t('filters.from')}>
        <input
          type="date"
          value={params.get('from') ?? ''}
          onChange={(e) => update('from', e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-1"
        />
      </Filter>

      <Filter label={t('filters.to')}>
        <input
          type="date"
          value={params.get('to') ?? ''}
          onChange={(e) => update('to', e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-1"
        />
      </Filter>

      {hideBuckets ? null : null}

      <Button
        variant="ghost"
        size="sm"
        className="ml-auto"
        onClick={() => setParams(new URLSearchParams(), { replace: true })}
      >
        {t('filters.clear')}
      </Button>
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
